
import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LanguageSelector } from '@/components/ui/language-selector';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Home, BarChart3, Calculator, TrendingUp, Info, User, LogOut } from 'lucide-react';

interface HeaderProps {
  isScrolled?: boolean;
  nameOpacity?: number;
  logoOpacity?: number;
}

const Header = ({ isScrolled = false, nameOpacity = 1, logoOpacity = 1 }: HeaderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const location = useLocation();
  const { user, isGuest, signOut } = useAuth();
  
  const shouldApplyFade = location.pathname === '/dashboard' || location.pathname === '/about';
  const navItemOpacity = shouldApplyFade ? nameOpacity : 1;

  const getInitials = () => {
    return user?.email?.slice(0, 2).toUpperCase() || 'U';
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };
  
  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-background/80 backdrop-blur-xl shadow-[0_1px_0_0_hsl(var(--border)/0.5)]' 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2.5">
            <div 
              className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center transition-opacity duration-300" 
              style={{ opacity: logoOpacity }}
            >
              <span className="text-primary-foreground font-bold text-lg">D</span>
            </div>
            <span 
              className="text-xl font-semibold transition-opacity duration-300 tracking-tight" 
              style={{ opacity: nameOpacity }}
            >DCFlow</span>
          </Link>
          
          <nav className="hidden md:flex items-center space-x-7 transition-opacity duration-300" style={{ opacity: navItemOpacity }}>
            {[
              { to: '/', label: t('nav.home'), icon: Home },
              { to: '/dashboard', label: t('nav.dashboard'), icon: BarChart3 },
              { to: '/calculators', label: t('nav.calculators'), icon: Calculator },
              { to: '/market', label: t('nav.market'), icon: TrendingUp },
              { to: '/about', label: t('nav.about'), icon: Info },
            ].map(({ to, label, icon: Icon }) => (
              <Link 
                key={to}
                to={to} 
                className={`group text-sm font-medium transition-colors duration-200 flex items-center gap-1.5 ${
                  location.pathname === to 
                    ? 'text-primary' 
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon size={16} strokeWidth={2} className="transition-transform duration-200 group-hover:scale-110" />
                {label}
              </Link>
            ))}
          </nav>
          
          <div className="hidden md:flex items-center space-x-2 transition-opacity duration-300" style={{ opacity: navItemOpacity }}>
            <ThemeToggle />
            <LanguageSelector />
            {!isGuest && user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 p-0">
                    <Avatar className="h-9 w-9 border border-border">
                      <AvatarFallback className="bg-primary text-primary-foreground text-xs font-semibold">
                        {getInitials()}
                      </AvatarFallback>
                    </Avatar>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <div className="px-2 py-1.5">
                    <p className="text-sm font-medium text-foreground truncate">{user.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User size={16} className="mr-2" />
                    {t('nav.profile')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                    <BarChart3 size={16} className="mr-2" />
                    {t('nav.dashboard')}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive focus:text-destructive">
                    <LogOut size={16} className="mr-2" />
                    {t('profile.signOut')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button 
                onClick={() => navigate('/auth')}
                size="sm"
                className="bg-primary hover:bg-primary/90 text-primary-foreground active:scale-[0.97] transition-all duration-200"
                aria-label="Get Started"
              >
                {t('nav.getStarted')}
              </Button>
            )}
          </div>
          
          <button
            className="md:hidden p-2 rounded-lg hover:bg-muted/50 transition-colors duration-200"
            onClick={() => setIsOpen(!isOpen)}
            aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
            aria-expanded={isOpen}
          >
            <div className="relative w-5 h-4">
              <span className={`absolute block w-5 h-[1.5px] bg-current rounded-full transition-all duration-300 ${isOpen ? 'rotate-45 top-[7px]' : 'top-0'}`} />
              <span className={`absolute block w-5 h-[1.5px] bg-current rounded-full transition-all duration-300 ${isOpen ? 'opacity-0' : 'top-[7px]'}`} />
              <span className={`absolute block w-5 h-[1.5px] bg-current rounded-full transition-all duration-300 ${isOpen ? '-rotate-45 top-[7px]' : 'top-[14px]'}`} />
            </div>
          </button>
        </div>
      </div>
      
      {/* Mobile menu overlay */}
      {isOpen && (
        <div 
          className="md:hidden fixed inset-0 top-[72px] bg-black/20 z-40"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
      
      {/* Mobile menu panel */}
      <div 
        className={`md:hidden fixed top-[72px] left-0 right-0 z-50 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isOpen 
            ? 'translate-y-0 opacity-100' 
            : '-translate-y-2 opacity-0 pointer-events-none'
        }`}
      >
        <div className="bg-background border-t border-border shadow-xl mx-3 rounded-xl overflow-hidden">
          <div className="px-4 py-5">
            <nav className="flex flex-col space-y-0.5">
              {[
                { to: '/', label: t('nav.home'), icon: Home },
                { to: '/dashboard', label: t('nav.dashboard'), icon: BarChart3 },
                { to: '/calculators', label: t('nav.calculators'), icon: Calculator },
                { to: '/market', label: t('nav.market'), icon: TrendingUp },
                { to: '/about', label: t('nav.about'), icon: Info },
                ...(!isGuest && user ? [{ to: '/profile', label: t('nav.profile'), icon: User }] : []),
              ].map(({ to, label, icon: Icon }) => (
                <Link 
                  key={to}
                  to={to} 
                  className={`group py-3 px-4 rounded-lg text-[15px] font-medium transition-colors duration-150 flex items-center gap-2.5 ${
                    location.pathname === to 
                      ? 'text-primary bg-primary/8' 
                      : 'text-foreground hover:bg-muted'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <Icon size={18} strokeWidth={2} className="transition-transform duration-200 group-hover:scale-110" />
                  {label}
                </Link>
              ))}
            </nav>
            
            <div className="pt-4 mt-3 border-t border-border flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <ThemeToggle />
                <LanguageSelector />
              </div>
              {!isGuest && user ? (
                <Button 
                  variant="destructive"
                  onClick={() => { handleSignOut(); setIsOpen(false); }}
                  className="flex-1 active:scale-[0.97] transition-all duration-200"
                >
                  <LogOut size={16} className="mr-1.5" />
                  {t('profile.signOut')}
                </Button>
              ) : (
                <Button 
                  onClick={() => { navigate('/auth'); setIsOpen(false); }}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground flex-1 active:scale-[0.97] transition-all duration-200"
                  aria-label="Go to auth and close menu"
                >
                  {t('nav.getStarted')}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
